import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { ListChecks, MessageSquare, Pencil, Plus, Trash2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUSES = [
  { value: 'idee', label: 'Idee', className: 'bg-slate-100 text-slate-700' },
  { value: 'gepland', label: 'Gepland', className: 'bg-blue-100 text-blue-700' },
  { value: 'in_behandeling', label: 'In behandeling', className: 'bg-amber-100 text-amber-700' },
  { value: 'klaar', label: 'Klaar', className: 'bg-emerald-100 text-emerald-700' },
  { value: 'afgewezen', label: 'Afgewezen', className: 'bg-red-100 text-red-700' },
];

const PRIORITIES = [
  { value: 'laag', label: 'Laag', className: 'bg-slate-100 text-slate-600' },
  { value: 'normaal', label: 'Normaal', className: 'bg-blue-100 text-blue-700' },
  { value: 'hoog', label: 'Hoog', className: 'bg-amber-100 text-amber-700' },
];

const MODULES = [
  'Algemeen', 'Dashboard', 'Kalender & boekingen', 'Resources', 'Relaties', 'Ledenbestand',
  'Connectgroepen', 'Worship', 'Sleutelbeheer', 'Voorraadbeheer', 'Gebouwenbeheer', 'Heating',
  'Commerce', 'Connectlessen', 'Contractbeheer', 'Bibliotheek', 'Team & instellingen',
];

const EMPTY_FORM = { title: '', description: '', module: 'Algemeen', priority: 'normaal' };

const getLabel = (options, value) => options.find((option) => option.value === value)?.label || value || '-';
const getClassName = (options, value) => options.find((option) => option.value === value)?.className || 'bg-slate-100 text-slate-700';
const formatDate = (value) => (value ? new Date(value).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' }) : '-');

const UsecasesPage = () => {
  const { user, hasCapability } = useAuth();
  const canManage = hasCapability('usecases_manage');

  const [usecases, setUsecases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [moduleFilter, setModuleFilter] = useState('all');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUsecase, setEditingUsecase] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);

  const [commentsUsecase, setCommentsUsecase] = useState(null);
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState('');

  const [searchParams] = useSearchParams();
  const deepLinkHandledRef = useRef(false);

  useEffect(() => {
    fetchUsecases();
  }, []);

  const fetchUsecases = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/usecases`);
      setUsecases(response.data);
    } catch (error) {
      toast.error('Kon usecases niet laden');
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => usecases.filter((usecase) => {
    const statusMatches = statusFilter === 'all' || usecase.status === statusFilter;
    const moduleMatches = moduleFilter === 'all' || usecase.module === moduleFilter;
    return statusMatches && moduleMatches;
  }), [usecases, statusFilter, moduleFilter]);

  const stats = useMemo(() => STATUSES.map((status) => ({
    ...status,
    count: usecases.filter((usecase) => usecase.status === status.value).length,
  })), [usecases]);

  const canDelete = () => canManage;

  const resetForm = () => { setEditingUsecase(null); setFormData(EMPTY_FORM); };

  const openCreateDialog = () => { resetForm(); setDialogOpen(true); };
  const openEditDialog = (usecase) => {
    setEditingUsecase(usecase);
    setFormData({
      title: usecase.title || '',
      description: usecase.description || '',
      module: usecase.module || 'Algemeen',
      priority: usecase.priority || 'normaal',
    });
    setDialogOpen(true);
  };

  const submitUsecase = async (event) => {
    event.preventDefault();
    if (!canManage) return;
    if (!formData.title) { toast.error('Titel is verplicht'); return; }
    try {
      if (editingUsecase) {
        await axios.put(`${API}/usecases/${editingUsecase.id}`, formData);
        toast.success('Usecase bijgewerkt');
      } else {
        await axios.post(`${API}/usecases`, formData);
        toast.success('Usecase toegevoegd');
      }
      setDialogOpen(false);
      resetForm();
      fetchUsecases();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Actie mislukt');
    }
  };

  const openComments = async (usecase) => {
    setCommentsUsecase(usecase);
    setCommentText('');
    try {
      const response = await axios.get(`${API}/usecases/${usecase.id}/comments`);
      setComments(response.data);
    } catch (error) {
      toast.error('Kon reacties niet laden');
    }
  };

  // Deep link from the "nieuwe usecase" notification email (?usecase=<id>):
  // auto-open its comments/detail dialog once the list has loaded. Silently
  // does nothing if the id isn't in this user's org-scoped list -- /usecases
  // already only returns usecases for current_user's organization_id, so
  // this can't leak a usecase from another organization.
  useEffect(() => {
    if (deepLinkHandledRef.current || loading) return;
    const targetId = searchParams.get('usecase');
    if (!targetId) return;
    const target = usecases.find((item) => item.id === targetId);
    if (target) {
      deepLinkHandledRef.current = true;
      openComments(target);
    }
  }, [loading, usecases, searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  const submitComment = async (event) => {
    event.preventDefault();
    if (!canManage) return;
    if (!commentText.trim()) return;
    try {
      await axios.post(`${API}/usecases/${commentsUsecase.id}/comments`, { tekst: commentText.trim() });
      setCommentText('');
      const response = await axios.get(`${API}/usecases/${commentsUsecase.id}/comments`);
      setComments(response.data);
      fetchUsecases();
    } catch (error) {
      toast.error('Kon reactie niet toevoegen');
    }
  };

  const changeStatus = async (usecase, status) => {
    if (!canManage) return;
    try {
      await axios.put(`${API}/usecases/${usecase.id}`, { status });
      setUsecases((prev) => prev.map((item) => (item.id === usecase.id ? { ...item, status } : item)));
    } catch (error) {
      toast.error('Status wijzigen mislukt');
    }
  };

  const deleteUsecase = async (usecase) => {
    if (!window.confirm(`Usecase "${usecase.title}" verwijderen?`)) return;
    try {
      await axios.delete(`${API}/usecases/${usecase.id}`);
      toast.success('Usecase verwijderd');
      setUsecases((prev) => prev.filter((item) => item.id !== usecase.id));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Verwijderen mislukt');
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-slate-200 rounded w-56"></div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {[1, 2, 3, 4, 5].map((item) => (
              <div key={item} className="h-20 bg-slate-200 rounded-2xl"></div>
            ))}
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="usecases-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-['Manrope']">Usecases</h1>
            <p className="text-slate-500">Gedeeld overzicht voor de werkgroep: meld een usecase en volg samen de voortgang</p>
          </div>
          {canManage && <Button onClick={openCreateDialog} className="bg-teal-600 hover:bg-teal-700 text-white rounded-xl w-full sm:w-auto">
            <Plus className="w-4 h-4 mr-2" /> Nieuwe usecase
          </Button>}
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          {stats.map((status) => (
            <Card key={status.value} className="border-0 shadow-sm">
              <CardContent className="p-4">
                <p className="text-sm text-slate-500">{status.label}</p>
                <p className="text-2xl font-bold text-slate-900">{status.count}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="grid sm:grid-cols-2 gap-3">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="rounded-xl"><SelectValue placeholder="Filter op status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle statussen</SelectItem>
                  {STATUSES.map((status) => <SelectItem key={status.value} value={status.value}>{status.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={moduleFilter} onValueChange={setModuleFilter}>
                <SelectTrigger className="rounded-xl"><SelectValue placeholder="Filter op module" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle modules</SelectItem>
                  {MODULES.map((moduleName) => <SelectItem key={moduleName} value={moduleName}>{moduleName}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="p-0">
            {filtered.length === 0 ? (
              <div className="p-12 text-center">
                <ListChecks className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-900 mb-2">Nog geen usecases</h3>
                <p className="text-slate-500">Meld de eerste usecase of het eerste aandachtspunt vanuit de werkgroep.</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {filtered.map((usecase) => (
                  <div key={usecase.id} className="flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-4 p-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-slate-900">{usecase.title}</span>
                        <span className="text-[11px] font-semibold px-2 py-0.5 rounded bg-slate-100 text-slate-700">{usecase.module || 'Algemeen'}</span>
                        <span className={`text-[11px] font-semibold px-2 py-0.5 rounded ${getClassName(PRIORITIES, usecase.priority)}`}>
                          {getLabel(PRIORITIES, usecase.priority)}
                        </span>
                      </div>
                      {usecase.description && <p className="text-sm text-slate-500 mt-1">{usecase.description}</p>}
                      <p className="text-xs text-slate-400 mt-1">Door {usecase.created_by_name} · {formatDate(usecase.created_at)}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Select disabled={!canManage} value={usecase.status} onValueChange={(value) => changeStatus(usecase, value)}>
                        <SelectTrigger className={`rounded-xl w-full sm:w-[160px] border-0 text-xs font-semibold ${getClassName(STATUSES, usecase.status)}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {STATUSES.map((status) => <SelectItem key={status.value} value={status.value}>{status.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <Button variant="ghost" size="sm" onClick={() => openComments(usecase)} className="relative">
                        <MessageSquare className="w-4 h-4" />
                        {usecase.comment_count > 0 && (
                          <span className="absolute -top-1 -right-1 text-[10px] font-semibold bg-teal-600 text-white rounded-full w-4 h-4 flex items-center justify-center">
                            {usecase.comment_count}
                          </span>
                        )}
                      </Button>
                      {canManage && <Button variant="ghost" size="sm" onClick={() => openEditDialog(usecase)}>
                        <Pencil className="w-4 h-4" />
                      </Button>}
                      {canDelete(usecase) && (
                        <Button variant="ghost" size="sm" onClick={() => deleteUsecase(usecase)} className="text-red-600">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>{editingUsecase ? 'Usecase bewerken' : 'Nieuwe usecase'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={submitUsecase} className="space-y-4">
              <div className="space-y-2">
                <Label>Titel *</Label>
                <Input value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} placeholder="Bijv. Boek reserveren voor een groep" className="rounded-xl" />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Module</Label>
                  <Select value={formData.module} onValueChange={(v) => setFormData({ ...formData, module: v })}>
                    <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {MODULES.map((moduleName) => <SelectItem key={moduleName} value={moduleName}>{moduleName}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Prioriteit</Label>
                  <Select value={formData.priority} onValueChange={(v) => setFormData({ ...formData, priority: v })}>
                    <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {PRIORITIES.map((priority) => <SelectItem key={priority.value} value={priority.value}>{priority.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Omschrijving</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Wat wil je kunnen doen, en waarom?"
                  className="rounded-xl resize-none"
                  rows={4}
                />
              </div>
              <DialogFooter className="gap-2">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-xl">Annuleren</Button>
                <Button type="submit" className="bg-teal-600 hover:bg-teal-700 text-white rounded-xl">{editingUsecase ? 'Opslaan' : 'Toevoegen'}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        <Dialog open={Boolean(commentsUsecase)} onOpenChange={(open) => { if (!open) { setCommentsUsecase(null); setComments([]); } }}>
          <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Reacties — {commentsUsecase?.title}</DialogTitle></DialogHeader>
            {canManage && <form onSubmit={submitComment} className="space-y-3 border-b border-slate-100 pb-4">
              <Textarea value={commentText} onChange={(e) => setCommentText(e.target.value)} placeholder="Reageer op deze usecase..." className="rounded-xl resize-none" rows={2} />
              <div className="flex justify-end"><Button type="submit" className="bg-teal-600 hover:bg-teal-700 text-white rounded-xl">Reageren</Button></div>
            </form>}
            <div className="divide-y divide-slate-100 max-h-72 overflow-y-auto">
              {comments.length === 0 ? (
                <p className="py-4 text-sm text-slate-500">Nog geen reacties.</p>
              ) : (
                comments.map((comment) => (
                  <div key={comment.id} className="py-2.5">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-slate-900">{comment.user_name}</span>
                      <span className="text-xs text-slate-400">{formatDate(comment.created_at)}</span>
                    </div>
                    <p className="text-sm text-slate-600 mt-0.5 whitespace-pre-wrap">{comment.tekst}</p>
                  </div>
                ))
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default UsecasesPage;
